/**
 * @file lib/types/config.ts
 * @description Actor SDK Config 設定システム
 * @author GFTD Co., Ltd.
 */

import { SessionConfig } from './session'
import { GitConfig } from '../git/git-fs'
import { ChatGitConfig } from './chat-git'

/**
 * GFTD認証設定
 */
export interface GFTDAuthConfig {
  /** 認証必須かどうか */
  required?: boolean
  /** 認証サーバーURL */
  authUrl?: string
  /** 認証APIキー */
  apiKey?: string
  /** 認証トークンの有効期限 (秒) */
  tokenExpiry?: number
  /** 認証プロバイダー */
  provider?: 'gftd' | 'custom'
  /** 認証スコープ */
  scopes?: string[]
  /** 自動リフレッシュ設定 */
  autoRefresh?: boolean
  /** リダイレクトURL */
  redirectUrl?: string
}

/**
 * ツール機能設定
 */
export interface ToolsConfig {
  /** Actor管理機能 */
  actorManagement: boolean
  /** セッション管理機能 */
  sessionManagement: boolean
  /** ストリーミング機能 */
  streaming: boolean
  /** Embedding & Vector Search機能 */
  embedding: boolean
  /** Git統合機能 */
  gitIntegration: boolean
  /** チャット履歴Git管理 */
  chatGitManagement: boolean
  /** モニタリング & アナリティクス */
  monitoring: boolean
  /** React UI コンポーネント */
  uiComponents: boolean
}

/**
 * Actor API設定
 */
export interface ActorApiConfig {
  /** API ベース URL */
  baseUrl: string
  /** API キー */
  apiKey?: string
  /** タイムアウト設定 (秒) */
  timeout?: number
  /** リトライ回数 */
  retryCount?: number
  /** リクエストヘッダー */
  headers?: Record<string, string>
}

/**
 * Embedding設定
 */
export interface EmbeddingConfig {
  /** モデル名 */
  modelName?: string
  /** ベクトル次元数 */
  dimensions?: number
  /** キャッシュディレクトリ */
  cacheDir?: string
  /** バッチサイズ */
  batchSize?: number
}

/**
 * Git統合設定
 */
export interface GitIntegrationConfig {
  /** GitLab設定 */
  gitlab?: {
    url: string
    token: string
    defaultVisibility?: 'private' | 'internal' | 'public'
  }
  /** Git作成者情報 */
  author?: {
    name: string
    email: string
  }
  /** 自動コミット設定 */
  autoCommit?: boolean
  /** 自動プッシュ設定 */
  autoPush?: boolean
  /** デフォルトブランチ名 */
  defaultBranch?: string
}

/**
 * ストリーミング設定
 */
export interface StreamingConfig {
  /** ストリーミング有効化 */
  enabled: boolean
  /** チャンクサイズ */
  chunkSize?: number
  /** タイムアウト設定 (秒) */
  timeout?: number
  /** 再接続設定 */
  reconnect?: {
    enabled: boolean
    maxAttempts?: number
    interval?: number
  }
}

/**
 * モニタリング設定
 */
export interface MonitoringConfig {
  /** メトリクス収集 */
  metricsEnabled: boolean
  /** ログレベル */
  logLevel?: 'debug' | 'info' | 'warn' | 'error'
  /** パフォーマンス追跡 */
  performanceTracking?: boolean
  /** エラー自動レポート */
  errorReporting?: boolean
}

/**
 * UI設定
 */
export interface UIConfig {
  /** テーマ */
  theme?: 'light' | 'dark' | 'auto'
  /** 言語設定 */
  locale?: string
  /** デフォルトページサイズ */
  defaultPageSize?: number
  /** アニメーション有効化 */
  animations?: boolean
  /** カスタムCSS */
  customStyles?: string
}

/**
 * デバッグ設定
 */
export interface DebugConfig {
  /** デバッグモード */
  enabled: boolean
  /** 詳細ログ */
  verboseLogging?: boolean
  /** API呼び出しログ */
  logApiCalls?: boolean
  /** Git操作ログ */
  logGitOperations?: boolean
  /** パフォーマンス計測 */
  measurePerformance?: boolean
}

/**
 * Actor SDK 包括的設定
 */
export interface ActorSDKConfig {
  /** 有効化するtools */
  tools: ToolsConfig
  
  /** Actor API設定 */
  api: ActorApiConfig
  
  /** GFTD認証設定 */
  auth?: GFTDAuthConfig
  
  /** セッション管理のデフォルト設定 */
  session?: SessionConfig
  
  /** Embedding設定 */
  embedding?: EmbeddingConfig
  
  /** Git統合設定 */
  git?: GitIntegrationConfig
  
  /** チャット履歴Git管理設定 */
  chatGit?: ChatGitConfig
  
  /** ストリーミング設定 */
  streaming?: StreamingConfig
  
  /** モニタリング設定 */
  monitoring?: MonitoringConfig
  
  /** UI設定 */
  ui?: UIConfig
  
  /** デバッグ設定 */
  debug?: DebugConfig
  
  /** カスタム設定 */
  custom?: Record<string, any>
}

/**
 * デフォルト設定を生成
 */
export function createDefaultConfig(): ActorSDKConfig {
  return {
    api: {
      baseUrl: 'https://actor.gftd.ai/api/v3',
      timeout: 30000,
      retryCount: 3
    },
    tools: {
      actorManagement: true,
      sessionManagement: true,
      streaming: true,
      embedding: false,
      gitIntegration: false,
      chatGitManagement: false,
      monitoring: true,
      uiComponents: true
    },
    auth: {
      required: false,
      provider: 'gftd'
    },
    session: {
      max_iterations: 100,
      auto_save: true
    },
    streaming: {
      enabled: true,
      chunkSize: 1024,
      timeout: 60,
      reconnect: {
        enabled: true,
        maxAttempts: 3,
        interval: 5000
      }
    },
    monitoring: {
      metricsEnabled: true,
      logLevel: 'info',
      performanceTracking: false,
      errorReporting: false
    },
    ui: {
      theme: 'auto',
      locale: 'ja',
      defaultPageSize: 20,
      animations: true
    },
    debug: {
      enabled: false,
      verboseLogging: false,
      logApiCalls: false,
      logGitOperations: false,
      measurePerformance: false
    }
  }
}

/**
 * 開発環境用設定を生成
 */
export function createDevelopmentConfig(): ActorSDKConfig {
  const baseConfig = createDefaultConfig()
  return {
    ...baseConfig,
    api: {
      ...baseConfig.api,
      baseUrl: 'https://actor.gftd.ai/api/v3'
    },
    tools: {
      ...baseConfig.tools,
      gitIntegration: true,
      chatGitManagement: true,
      embedding: true,
      monitoring: true
    },
    auth: {
      required: false,
      provider: 'gftd'
    },
    debug: {
      enabled: true,
      logApiCalls: true,
      logGitOperations: true
    }
  }
}

/**
 * プロダクション環境用設定を生成
 */
export function createProductionConfig(): ActorSDKConfig {
  const baseConfig = createDefaultConfig()
  return {
    ...baseConfig,
    api: {
      ...baseConfig.api,
      baseUrl: 'https://actor.gftd.ai/api/v3'
    },
    auth: {
      required: true,
      provider: 'gftd'
    },
    debug: {
      enabled: false,
      logApiCalls: false,
      logGitOperations: false
    }
  }
}

/**
 * 設定をマージ
 */
export function mergeConfig(...configs: Partial<ActorSDKConfig>[]): ActorSDKConfig {
  // デフォルト設定をベースとして開始
  const baseConfig = {
    tools: {
      actorManagement: true,
      sessionManagement: true,
      streaming: true,
      embedding: false,
      gitIntegration: false,
      chatGitManagement: false,
      monitoring: true,
      uiComponents: true
    },
    api: {
      baseUrl: process.env.ACTOR_API_BASE_URL || 'https://api.gftd.ai',
      timeout: 30,
      retryCount: 3
    }
  } as ActorSDKConfig

  return configs.reduce<ActorSDKConfig>((acc, config) => {
    if (!config) return acc
    
    return {
      ...acc,
      ...config,
      tools: config.tools ? { ...acc.tools, ...config.tools } : acc.tools,
      api: config.api ? { ...acc.api, ...config.api } : acc.api,
      auth: config.auth ? { ...acc.auth, ...config.auth } : acc.auth,
      session: config.session ? { ...acc.session, ...config.session } : acc.session,
      embedding: config.embedding ? { ...acc.embedding, ...config.embedding } : acc.embedding,
      git: config.git ? { ...acc.git, ...config.git } : acc.git,
      chatGit: config.chatGit ? { ...acc.chatGit, ...config.chatGit } : acc.chatGit,
      streaming: config.streaming ? { ...acc.streaming, ...config.streaming } : acc.streaming,
      monitoring: config.monitoring ? { ...acc.monitoring, ...config.monitoring } : acc.monitoring,
      ui: config.ui ? { ...acc.ui, ...config.ui } : acc.ui,
      debug: config.debug ? { ...acc.debug, ...config.debug } : acc.debug,
      custom: config.custom ? { ...acc.custom, ...config.custom } : acc.custom,
    }
  }, baseConfig)
}

/**
 * 設定の検証
 */
export function validateConfig(config: ActorSDKConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // 必須フィールドのチェック
  if (!config.api?.baseUrl) {
    errors.push('api.baseUrl is required')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 設定が有効かチェック
 */
export function isToolEnabled(config: ActorSDKConfig, tool: keyof ToolsConfig): boolean {
  return config.tools[tool] === true
}

/**
 * 環境変数から設定を生成
 */
export function createConfigFromEnv(): Partial<ActorSDKConfig> {
  const config: Partial<ActorSDKConfig> = {}

  // API設定
  if (process.env.ACTOR_API_BASE_URL) {
    config.api = {
      baseUrl: process.env.ACTOR_API_BASE_URL,
      apiKey: process.env.ACTOR_API_KEY,
      timeout: process.env.ACTOR_API_TIMEOUT ? parseInt(process.env.ACTOR_API_TIMEOUT) : undefined,
    }
  }

  // GFTD認証設定（環境変数から生成）
  if (process.env.GFTD_AUTH_URL || process.env.GFTD_AUTH_API_KEY) {
    config.auth = {
      required: process.env.GFTD_AUTH_REQUIRED !== 'false',
      authUrl: process.env.GFTD_AUTH_URL || 'https://auth.gftd.ai',
      apiKey: process.env.GFTD_AUTH_API_KEY,
      provider: 'gftd',
      tokenExpiry: process.env.GFTD_AUTH_TOKEN_EXPIRY ? parseInt(process.env.GFTD_AUTH_TOKEN_EXPIRY) : 3600,
      scopes: process.env.GFTD_AUTH_SCOPES ? process.env.GFTD_AUTH_SCOPES.split(',') : ['read', 'write'],
      autoRefresh: process.env.GFTD_AUTH_AUTO_REFRESH !== 'false',
      redirectUrl: process.env.GFTD_AUTH_REDIRECT_URL
    }
  }
  // デバッグ設定
  if (process.env.NODE_ENV === 'development') {
    config.debug = {
      enabled: true,
      verboseLogging: true,
      logApiCalls: true,
      logGitOperations: true
    }
  }

  return config
}

/**
 * 開発環境設定を生成（統一されたAPIエンドポイント使用）
 */
export function createLocalDevelopmentConfig(organizationId?: string): ActorSDKConfig {
  const baseConfig = createDevelopmentConfig()
  return {
    ...baseConfig,
    api: {
      ...baseConfig.api,
      baseUrl: 'https://actor.gftd.ai/api/v3'
    },
    tools: {
      ...baseConfig.tools,
      gitIntegration: true,
      chatGitManagement: true,
      embedding: true,
      monitoring: true
    },
    auth: {
      required: false,
      provider: 'gftd'
    },
    debug: {
      enabled: true,
      logApiCalls: true,
      logGitOperations: true
    }
  }
}

/**
 * 環境判定: 開発環境かどうか
 */
export function isLocalDevelopment(): boolean {
  return process.env.NODE_ENV === 'development'
}

/**
 * GitLabデフォルト設定付きSDK設定を生成
 */
export function createConfigWithDefaultGitLab(overrides?: Partial<ActorSDKConfig>): ActorSDKConfig {
  const defaultGitLabConfig = {
    git: {
      autoCommit: process.env.GIT_AUTO_COMMIT === 'true',
      autoPush: process.env.GIT_AUTO_PUSH === 'true',
      defaultBranch: process.env.GIT_DEFAULT_BRANCH || 'main'
    },
    tools: {
      gitIntegration: true,
      chatGitManagement: true,
      actorManagement: true,
      sessionManagement: true,
      streaming: true,
      embedding: false,
      monitoring: true,
      uiComponents: true
    }
  }
  const baseConfig = createDefaultConfig()
  
  return mergeConfig(baseConfig, defaultGitLabConfig, overrides || {})
} 